import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Group, Modal, Stack, Title, Center, Alert, Loader } from "@mantine/core";
import VisaTable from "../../components/visa/VisaTable";
import VisaForm from "../../components/visa/VisaForm";
import type { VisaApplication } from "../../types/VisaApplication";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billlede-1.png";
import { IconPlus } from '@tabler/icons-react';
import "../../components/button.css";

export default function TeamVisaPage() {
    const { eventId } = useParams();
    const parsedEventId = Number(eventId);

    const userId = useMemo(
        () => Number(sessionStorage.getItem("userId") || "0"),
        []
    );

    const apiUrl = process.env.REACT_APP_API_URL;

    const [items, setItems] = useState<VisaApplication[]>([]);
    const [error, setError] = useState<string | null>(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [draft, setDraft] = useState<VisaApplication | null>(null);

    const [file, setFile] = useState<File | null>(null);

    const [createStatus, setCreateStatus] = useState<
        | { type: "success"; message: string }
        | { type: "warning"; message: string }
        | { type: "error"; message: string }
        | null
        >(null);
    const [creating, setCreating] = useState(false);



    const load = async () => {
        setError(null);

        const response = await fetch(
            `${apiUrl}/api/VisaApplications?eventId=${eventId}&createdByUserId=${userId}`
        );

        if (!response.ok) {
            const text = await response.text().catch(() => "");
            throw new Error(`GET failed: ${response.status} - ${text}`);
        }

        setItems(await response.json());
    };

    useEffect(() => {
        load().catch((err) => {
            console.error(err);
            setError(err.message || "Ukendt fejl ved load");
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const openCreate = () => {
        if (!eventId || !userId) return;

        // Lav en tom draft når modal åbner
        setDraft({
            id: 0,
            personIDFK: 0,
            eventIDFK: parsedEventId,
            createdByUserId: userId,

            firstName: "",
            middleName: null,
            lastName: "",

            dateOfBirth: null,
            expiryDate: null,
            issueDate: null,

            nationality: null,
            passportNo: null,

            placeOfBirth: null,
            cityToApply: null,

            email: null,
            invitationLetterFile: null,

            sent: false
        });

        setFile(null);
        setCreateOpen(true);
    };

    const createVisa = async () => {
        try {
            setError(null);
            setCreateStatus(null);

            if (!apiUrl) return setError("REACT_APP_API_URL mangler");
            if (!draft) return setError("Draft mangler");
            if (!draft.firstName || !draft.lastName)
                return setError("First name og Last name skal udfyldes");

            setCreating(true);

            const payload = {
                eventIDFK: draft.eventIDFK,
                personIDFK: draft.personIDFK,
                createdByUserId: draft.createdByUserId,
                firstName: draft.firstName,
                middleName: draft.middleName,
                lastName: draft.lastName,
                dateOfBirth: draft.dateOfBirth,
                expiryDate: draft.expiryDate,
                issueDate: draft.issueDate,
                nationality: draft.nationality,
                passportNo: draft.passportNo,
                placeOfBirth: draft.placeOfBirth,
                cityToApply: draft.cityToApply,
                email: draft.email,
                invitationLetterFile: null,
                sent: false,
            };

            const response = await fetch(`${apiUrl}/api/VisaApplications`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                const msg = result?.message || `POST failed: ${response.status}`;
                throw new Error(msg);
            }

            if (result.mailSent) {
                setCreateStatus({
                    type: "success",
                    message: "Thank you for your visa application. We have received it and sent a message to the organizer ✅",
                });
            } else {
                setCreateStatus({
                    type: "warning",
                    message:
                        "Thank you for your visa application.We have received it, but could not send the email right now ⚠️",
                });
            }

            await load();

            // valgfrit: luk modal efter lidt tid
            setTimeout(() => {
                setCreateOpen(false);
                setDraft(null);
                setFile(null);
            }, 1200);
        } catch (err: any) {
            console.error(err);
            setCreateStatus({ type: "error", message: err?.message || "Noget gik galt" });
        } finally {
            setCreating(false);
        }
    };


    return (
        <div>
        <HeroSectionV2
                titleEn="Team booking"
                titleDa="Team booking"
                descriptionEn={"Visa"}
                descriptionDa={"Visrum"}
                imageSrc={Billede1}
                badgeText="TEAM"
                    />
        <Center my="md">
                <Title order={1}>Visum</Title>

        </Center>
        <Stack>
            {error && <div style={{ color: "red" }}>{error}</div>}

            <Group justify="center">
                    <Button
                        className="eventCardV2__btn"
                        style={{ width: "auto", alignSelf: "center" }}
                        onClick={openCreate}
                        rightSection={<IconPlus size={14} />}
                    >
                    Create visa request
                </Button>
                </Group>
                {createStatus && (
                    <Center my="md">
                    <Alert
                        color={
                            createStatus.type === "success"
                                ? "green"
                                : createStatus.type === "warning"
                                    ? "yellow"
                                    : "red"
                        }
                        variant="light"
                    >
                        {createStatus.message}
                        </Alert>
                    </Center>
                )}
            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                    title="Create visa request"
                size="lg"
                centered
                styles={{
                    body: {
                        maxHeight: "70vh",
                        overflowY: "auto",
                    },
                }}
                >
                {draft && (
                        <>


                            {creating && (
                                <Group mb="md" gap="sm">
                                    <Loader size="sm" />
                                    <div>Opretter visa request…</div>
                                </Group>
                            )}

                        <VisaForm
                            value={draft}
                            onChange={(next) => setDraft(next)}
                            mode="create"
                            file={file}
                            setFile={setFile}
                            showInvitation={false}
                            showSent={false}
                        />

                            <Group justify="flex-end" mt="md">
                                <Button  variant="light" onClick={() => setCreateOpen(false)} disabled={creating}>
                                    Cancel
                                </Button>
                                <Button className="eventCardV2__btn" onClick={createVisa} loading={creating} disabled={creating}>
                                    Create
                                </Button>
                            </Group>

                    </>
                )}
            </Modal>
            <Center>
            <VisaTable
                title="View visa requests"
                data={items}
                mode="user"
                showStatusInUser={true}
                    />
            </Center>
            </Stack>


    </div>
    );
}

